import { useMutation } from '@apollo/client'
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Collapse,
    IconButton,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons'
import React, { useState } from 'react'
import { RESOLVE_CLASH } from '../../api/graphQL/mutations/bestPracticeClash.mutations'
import { BestPracticeClash } from '../../api/graphQL/types/bestPracticeClash.types'

const useStyles = makeStyles((theme) => ({
    card: {
        marginBottom: theme.spacing(2),
        transition: 'all 0.3s ease',
        border: `1px solid ${theme.palette.divider}`,
        width: '100%',
        boxSizing: 'border-box',
    },
    resolvedCard: {
        backgroundColor: theme.palette.grey[100],
        opacity: 0.7,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        width: '100%',
    },
    content: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
    },

    expandButton: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandButtonOpen: {
        transform: 'rotate(180deg)',
    },
    detailsSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
    contentPreview: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        fontStyle: 'italic',
    },
}))

interface ClashCardProps {
    clash: BestPracticeClash
    onResolved?: (clashId: string) => void
}

export const ClashCard: React.FC<ClashCardProps> = ({ clash, onResolved }) => {
    const classes = useStyles()
    const [expanded, setExpanded] = useState(false)

    const [resolveClash, { loading }] = useMutation(RESOLVE_CLASH, {
        onCompleted: () => {
            if (onResolved) {
                onResolved(clash.id)
            }
        },
    })

    const handleResolve = () => {
        if (!clash.isResolved) {
            resolveClash({ variables: { input: { clashId: clash.id } } })
        }
    }

    return (
        <Card
            className={`${classes.card} ${
                clash.isResolved ? classes.resolvedCard : ''
            }`}
            elevation={clash.isResolved ? 0 : 2}
        >
            <CardContent>
                <Box className={classes.header}>
                    <Box className={classes.content}>
                        <Typography
                            variant="subtitle1"
                            gutterBottom
                            style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                            }}
                        >
                            {clash.productFamilyName
                                ? `Clash in ${clash.productFamilyName} (${clash.familyNumber})`
                                : 'Best Practice Clash'}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                            }}
                        >
                            {clash.errorMessage}
                        </Typography>

                        {clash.contentTextPreview && (
                            <Box className={classes.contentPreview}>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    style={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    {clash.contentTextPreview}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Checkbox
                        checked={clash.isResolved}
                        onChange={handleResolve}
                        color="primary"
                        disabled={clash.isResolved || loading}
                    />
                    <IconButton
                        className={`${classes.expandButton} ${
                            expanded ? classes.expandButtonOpen : ''
                        }`}
                        onClick={() => setExpanded(!expanded)}
                        size="small"
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box className={classes.detailsSection}>
                        {/* <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                        >
                            <strong>Project Element:</strong>{' '}
                            {clash.projectElementNodeId}
                        </Typography> */}

                        <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                        >
                            <strong>Tree Path:</strong> {clash.treePath}
                        </Typography>

                        {/* <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                        >
                            <strong>Best Practice:</strong>{' '}
                            {clash.bestPracticeId}
                        </Typography> */}

                        <Typography
                            variant="caption"
                            display="block"
                            color="textSecondary"
                        >
                            <strong>Detected:</strong>{' '}
                            {new Date(clash.metadata.created).toLocaleString()}
                        </Typography>

                        {clash.isResolved && clash.resolvedDate && (
                            <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                            >
                                <strong>Resolved:</strong>{' '}
                                {new Date(clash.resolvedDate).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    )
}
